import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Grid3x3, ExternalLink, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

// Derive slug from app name (matches AppCard.js logic)
const getSlug = (app) => {
    if (app.slug) return app.slug;
    const name = (app.name || '').toLowerCase();
    if (name.includes('galeri')) return 'galeri';
    if (name.includes('emlak')) return 'emlak';
    if (name.includes('lojistik') || name.includes('nakliyat')) return 'lojistik';
    return app.id;
};

/**
 * App Switcher - Quick jump between Ti-Cari ecosystem applications.
 * Shows all active applications as a 3x grid. Inactive (coming_soon) apps
 * are shown disabled with a "Yakında" badge.
 */
export const AppSwitcher = () => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.getApplications()
            .then((data) => { if (mounted) setApps(Array.isArray(data) ? data : []); })
            .catch(() => {})
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    data-testid="app-switcher-trigger"
                    aria-label="Uygulama seçici"
                >
                    <Grid3x3 className="w-5 h-5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-3" data-testid="app-switcher-content">
                <div className="px-1 pb-3 mb-2 border-b border-border">
                    <p className="text-foreground text-sm font-bold flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-[#C5A267]" />
                        Ti-Cari Ekosistemi
                    </p>
                    <p className="text-muted-foreground text-[11px] mt-0.5">MacTech Software · Tüm uygulamalar</p>
                </div>
                {loading ? (
                    <p className="text-center text-muted-foreground text-xs py-4" data-testid="app-switcher-loading">Yükleniyor...</p>
                ) : apps.length === 0 ? (
                    <p className="text-center text-muted-foreground text-xs py-4">Uygulama bulunamadı</p>
                ) : (
                    <div className="grid grid-cols-3 gap-2">
                        {apps.map((app) => {
                            const isActive = app.status === 'active';
                            const slug = getSlug(app);
                            const Inner = (
                                <div
                                    className={`relative aspect-square rounded-xl border flex flex-col items-center justify-center text-center p-2 transition-all ${
                                        isActive
                                            ? 'border-border hover:border-[#C5A267]/60 hover:bg-[#C5A267]/5 cursor-pointer'
                                            : 'border-border/40 opacity-50 cursor-not-allowed'
                                    }`}
                                    data-testid={`app-switcher-item-${slug}`}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C5A267] to-[#A68A50] flex items-center justify-center mb-1.5 overflow-hidden">
                                        {app.icon_url || app.logo_url ? (
                                            <img
                                                src={app.icon_url || app.logo_url}
                                                alt={app.name}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        ) : (
                                            <span className="text-black font-extrabold text-sm">
                                                {(app.name || 'A').charAt(0)}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-foreground text-[11px] font-semibold leading-tight line-clamp-2">{app.name}</span>
                                    {!isActive && (
                                        <span className="absolute top-1 right-1 text-[8px] font-bold uppercase tracking-wider bg-[#C5A267]/15 text-[#C5A267] border border-[#C5A267]/30 rounded-full px-1.5 py-0.5">
                                            Yakında
                                        </span>
                                    )}
                                </div>
                            );
                            return isActive ? (
                                <Link key={app.id} to={`/uygulama/${slug}`}>{Inner}</Link>
                            ) : (
                                <div key={app.id}>{Inner}</div>
                            );
                        })}
                    </div>
                )}
                <div className="mt-3 pt-3 border-t border-border">
                    <Link
                        to="/uygulamalar"
                        className="flex items-center justify-center gap-1.5 text-xs text-[#C5A267] hover:text-[#D4B98D] font-semibold py-1.5"
                        data-testid="app-switcher-all-apps"
                    >
                        Tüm uygulamaları gör
                        <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default AppSwitcher;
